import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const months = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

export default function LeaveTracker() {
  const [data, setData] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDept, setSelectedDept] = useState("All");
  const scrollRef = useRef();

  const currentYear = new Date().getFullYear();
  const currentMonthIndex = new Date().getMonth();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");

        const res = await axios.get(
          `"https://ims.holdthechild.net"/api/leave-request/summary/tracker?year=${selectedYear}&department=${selectedDept}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );

        setData(res.data.data || {});
      } catch (error) {
        console.error("Failed to load tracker data:", error);
        setData({});
      }
    };

    fetchData();
  }, [selectedYear, selectedDept]);


  const scrollLeft = () => {
    scrollRef.current.scrollBy({ left: -300, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
  };

  const departments =
    data && typeof data === "object" && !Array.isArray(data)
      ? Object.keys(data)
      : [];

  // Improved tooltip text logic
  const getTooltipText = (monthIdx, count) => {
    const monthName = months[monthIdx];
    const now = new Date();
    const isCurrentYear = selectedYear === now.getFullYear();
    const isPastMonth = isCurrentYear && monthIdx < now.getMonth();
    const isThisMonth = isCurrentYear && monthIdx === now.getMonth();
    const isFutureMonth = isCurrentYear && monthIdx > now.getMonth();
    const isFutureYear = selectedYear > now.getFullYear();
    const isPastYear = selectedYear < now.getFullYear();

    if (count > 0) {
      if (isPastMonth || isPastYear) {
        return `${monthName} had ${count} staff on leave`;
      } else if (isThisMonth) {
        return `${monthName} has ${count} staff on leave`;
      } else {
        // Future month or year
        return `${monthName} will have ${count} staff on leave`;
      }
    } else {
      // count === 0
      if (isPastMonth || isPastYear) {
        return `${monthName} had no staff on leave`;
      } else {
        // future month or current/future year with no leave yet
        return `Staff have not yet requested leave in ${monthName}`;
      }
    }
  };

  return (
    <div className="bg-white p-6 md:p-8 rounded-2xl shadow-md col-span-2">
      <div className="flex flex-wrap justify-between items-center mb-4 gap-4">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">Leave Tracker</h2>

        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-600">Year</label>
            <select
              className="border px-2 py-1 rounded-md"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600">Department</label>
            <select
              className="border px-2 py-1 rounded-md"
              value={selectedDept}
              onChange={(e) => setSelectedDept(e.target.value)}
            >
              <option value="All">All</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div className="space-x-2">
            <button onClick={scrollLeft} className="text-gray-600 hover:text-gray-900 text-xl">‹</button>
            <button onClick={scrollRight} className="text-gray-600 hover:text-gray-900 text-xl">›</button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto" ref={scrollRef}>
        <table className="min-w-max w-full border-separate border-spacing-y-2">
          <thead>
            <tr>
              <th className="text-left px-2 py-3 text-sm font-medium text-gray-600 w-32 shrink-0">Department</th>
              {months.map((month, idx) => (
                <th
                  key={idx}
                  className="text-center px-2 py-3 text-xs font-medium text-gray-600 min-w-[70px]"
                >
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {departments.length === 0 ? (
              <tr>
                <td colSpan={13} className="text-center py-4 text-gray-500">
                  No data available for the selected year or department.
                </td>
              </tr>
            ) : (
              departments.map((dept) => (
                <tr key={dept}>
                  <td className="text-xs font-semibold text-gray-700 px-2 py-2">{dept}</td>
                  {months.map((_, i) => {
                    const count = data[dept]?.[i + 1] || 0;
                    let color = "bg-orange-100";
                    if (count >= 10 && count < 50) color = "bg-orange-300";
                    if (count >= 50) color = "bg-orange-500";

                    const tooltip = getTooltipText(i, count);

                    return (
                      <td key={i} className="text-center px-2 py-2 relative group">
                        <div
                          className={`w-6 h-6 md:w-8 md:h-8 rounded-md ${color} cursor-default`}
                          aria-label={tooltip}
                        />
                        {/* Tooltip */}
                        <div className="absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 px-2 py-1 rounded bg-gray-700 text-white text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                          {tooltip}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap justify-center gap-4 text-xs md:text-sm text-gray-600">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 md:w-5 md:h-5 bg-orange-100 rounded-sm" />
          <span>Under 10</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 md:w-5 md:h-5 bg-orange-300 rounded-sm" />
          <span>10–49</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 md:w-5 md:h-5 bg-orange-500 rounded-sm" />
          <span>50+</span>
        </div>
      </div>
    </div>
  );
}
